import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

// ─── Notes Panel ─────────────────────────────────────────────────────────────

const NotesPanel = ({ budgetId }) => {
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [newNoteText, setNewNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [noteError, setNoteError] = useState('');
  const editInputRef = useRef(null);

  const loadNotes = async () => {
    try {
      const res = await axios.get(`/api/income_statement/budgets/${budgetId}/notes/`);
      setNotes(res.data || []);
    } catch (e) {
      setNoteError('Failed to load notes.');
    } finally {
      setLoadingNotes(false);
    }
  };

  useEffect(() => { if (budgetId) loadNotes(); }, [budgetId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (editingNoteId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingNoteId]);

  const handleAddNote = async () => {
    const text = newNoteText.trim();
    if (!text) return;
    setAddingNote(true); setNoteError('');
    try {
      const res = await axios.post(`/api/income_statement/budgets/${budgetId}/notes/`, { text });
      setNotes(prev => [res.data, ...prev]);
      setNewNoteText('');
    } catch (e) {
      setNoteError('Failed to add note.');
    } finally {
      setAddingNote(false);
    }
  };

  const startEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingText(note.text);
    setNoteError('');
  };

  const cancelEdit = () => {
    setEditingNoteId(null);
    setEditingText('');
  };

  const handleSaveEdit = async (noteId) => {
    const text = editingText.trim();
    if (!text) return;
    setSavingNote(true); setNoteError('');
    try {
      const res = await axios.patch(`/api/income_statement/budgets/${budgetId}/notes/${noteId}/`, { text });
      setNotes(prev => prev.map(n => n.id === noteId ? res.data : n));
      setEditingNoteId(null);
      setEditingText('');
    } catch (e) {
      setNoteError('Failed to save note.');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    setNoteError('');
    try {
      await axios.delete(`/api/income_statement/budgets/${budgetId}/notes/${noteId}/`);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (e) {
      setNoteError('Failed to delete note.');
    }
  };

  const formatNoteDate = (dt) => {
    try {
      const d = new Date(dt);
      return isNaN(d.getTime()) ? '' : d.toLocaleString();
    } catch (e) { return ''; }
  };

  return (
    <div style={{
      width: 300,
      minWidth: 260,
      maxWidth: 340,
      background: '#0b1220',
      border: '1px solid #374151',
      borderRadius: 8,
      display: 'flex',
      flexDirection: 'column',
      alignSelf: 'flex-start',
      position: 'sticky',
      top: 20,
    }}>
      <div style={{ padding: '12px 14px', borderBottom: '1px solid #374151' }}>
        <h3 style={{ margin: 0, fontSize: '1rem', color: '#e5e7eb' }}>📝 Notes</h3>
      </div>

      {/* Add new note */}
      <div style={{ padding: '10px 14px', borderBottom: '1px solid #1f2937' }}>
        <textarea
          rows={3}
          value={newNoteText}
          onChange={e => setNewNoteText(e.target.value)}
          placeholder="Add a note…"
          className="control-select"
          style={{ width: '100%', resize: 'vertical', fontSize: 13, boxSizing: 'border-box' }}
          onKeyDown={e => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleAddNote();
          }}
        />
        <div style={{ marginTop: 6, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            className="btn-primary"
            style={{ padding: '4px 14px', fontSize: 13 }}
            onClick={handleAddNote}
            disabled={addingNote || !newNoteText.trim()}
          >
            {addingNote ? 'Adding…' : 'Add Note'}
          </button>
        </div>
      </div>

      {/* Notes list */}
      <div style={{ padding: '8px 14px', overflowY: 'auto', maxHeight: '60vh' }}>
        {loadingNotes && <div style={{ color: '#9ca3af', fontSize: 13 }}>Loading notes…</div>}
        {!loadingNotes && notes.length === 0 && (
          <div style={{ color: '#6b7280', fontSize: 13, fontStyle: 'italic' }}>No notes yet.</div>
        )}
        {noteError && <div style={{ color: '#fca5a5', fontSize: 13, marginBottom: 6 }}>{noteError}</div>}
        <ul style={{ listStyle: 'disc', paddingLeft: 18, margin: 0 }}>
          {notes.map(note => (
            <li key={note.id} style={{ marginBottom: 14, color: '#d1d5db' }}>
              {editingNoteId === note.id ? (
                <div>
                  <textarea
                    ref={editInputRef}
                    rows={3}
                    value={editingText}
                    onChange={e => setEditingText(e.target.value)}
                    className="control-select"
                    style={{ width: '100%', resize: 'vertical', fontSize: 13, boxSizing: 'border-box' }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSaveEdit(note.id);
                      if (e.key === 'Escape') cancelEdit();
                    }}
                  />
                  <div style={{ marginTop: 4, display: 'flex', gap: 6 }}>
                    <button
                      className="btn-primary"
                      style={{ padding: '2px 10px', fontSize: 12 }}
                      onClick={() => handleSaveEdit(note.id)}
                      disabled={savingNote}
                    >
                      {savingNote ? '…' : 'Save'}
                    </button>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '2px 10px', fontSize: 12 }}
                      onClick={cancelEdit}
                      disabled={savingNote}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <span style={{ fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{note.text}</span>
                  <div style={{ marginTop: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: '#6b7280' }}>{formatNoteDate(note.created_at)}</span>
                    <button
                      style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', fontSize: 11, padding: 0 }}
                      onClick={() => startEditNote(note)}
                    >
                      Edit
                    </button>
                    <button
                      style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 11, padding: 0 }}
                      onClick={() => handleDeleteNote(note.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// ─── Asset group helpers ─────────────────────────────────────────────────────

const AUTOMOTIVE_CHILD_PREFIXES = ['automotive_', 'vehicle_licensing_tags'];
const HOME_CHILD_PREFIXES = ['home_'];

// Landscaping has no child categories — it is itself the leaf budget item, but still
// supports per-property budgeting. Amounts stored under the key '__self__' in the asset map.
const LEAF_ASSET_PARENTS = ['landscaping'];

const isAutomotiveChild = (slug) =>
  AUTOMOTIVE_CHILD_PREFIXES.some(p => slug === p || slug.startsWith(p));
const isHomeChild = (slug) =>
  HOME_CHILD_PREFIXES.some(p => slug === p || slug.startsWith(p));

// ─── Budget Detail Page ───────────────────────────────────────────────────────

const BudgetDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form state
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  const [expandedParents, setExpandedParents] = useState(new Set());

  // Form fields (mirrors BudgetsPage modal fields)
  const [budgetName, setBudgetName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [amounts, setAmounts] = useState({});
  const [subAmounts, setSubAmounts] = useState({});
  const [frequencies, setFrequencies] = useState({});
  const [monthlyOverrides, setMonthlyOverrides] = useState({});
  const [useMonthlyOverrides, setUseMonthlyOverrides] = useState({});
  const [splits, setSplits] = useState({});

  // Physical assets for per-asset budget hierarchy
  const [physicalAssets, setPhysicalAssets] = useState({ vehicles: [], properties: [] });
  // parentAssetMode: { [parentSlug]: 'total' | 'per_asset' }
  const [parentAssetMode, setParentAssetMode] = useState({});
  // assetAmounts: { [parentSlug]: { [assetId]: { [childSlug]: amountStr } } }
  const [assetAmounts, setAssetAmounts] = useState({});
  // savedAssetIds: { [parentSlug]: [assetId, ...] } — assets with saved budgets
  const [savedAssetIds, setSavedAssetIds] = useState({});
  // addingAssetFor: parentSlug | null
  const [addingAssetFor, setAddingAssetFor] = useState(null);
  // selectedNewAssetId: id of asset being configured
  const [selectedNewAssetId, setSelectedNewAssetId] = useState('');
  // draftAssetAmounts: { [childSlug]: amountStr } for the asset being added
  const [draftAssetAmounts, setDraftAssetAmounts] = useState({});
  // draftAssetChildMode: { [childSlug]: 'period' | 'fixed' } — per-child mode inside the per-asset draft form
  const [draftAssetChildMode, setDraftAssetChildMode] = useState({});
  // draftAssetMonthlyOverrides: { [childSlug]: { [month]: amountStr } } — monthly values when child mode is 'fixed'
  const [draftAssetMonthlyOverrides, setDraftAssetMonthlyOverrides] = useState({});

  // Debt payment state
  const [debtPaymentExpanded, setDebtPaymentExpanded] = useState(false);
  const [eligibleDebts, setEligibleDebts] = useState([]);
  const [debtAmounts, setDebtAmounts] = useState({});
  const [debtFrequencies, setDebtFrequencies] = useState({});
  const [debtPaymentDays, setDebtPaymentDays] = useState({});
  const [loadingDebts, setLoadingDebts] = useState(false);

  // ── Helpers (copied from BudgetsPage for consistency) ──

  const toMonthly = (amount, freq) => {
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt)) return 0;
    const count = Math.max(1, parseInt(freq?.count, 10) || 1);
    const unit = (freq?.unit || 'months');
    const WEEKS_PER_MONTH = 4;
    const DAYS_PER_MONTH = 30;
    switch (unit) {
      case 'days': return amt * (DAYS_PER_MONTH / count);
      case 'weeks': return amt * (WEEKS_PER_MONTH / count);
      case 'months': return amt * (1 / count);
      case 'years': return amt * (1 / (12 * count));
      default: return amt * (1 / count);
    }
  };

  const fromMonthly = (monthlyAmount, freq) => {
    const m = parseFloat(monthlyAmount);
    if (!Number.isFinite(m)) return 0;
    const count = Math.max(1, parseInt(freq?.count, 10) || 1);
    const unit = (freq?.unit || 'months');
    const WEEKS_PER_MONTH = 4;
    const DAYS_PER_MONTH = 30;
    switch (unit) {
      case 'days': return m * (count / DAYS_PER_MONTH);
      case 'weeks': return m * (count / WEEKS_PER_MONTH);
      case 'months': return m * count;
      case 'years': return m * (12 * count);
      default: return m * count;
    }
  };

  const childrenMonthlyTotal = (parentKey, subSnap = subAmounts, freqSnap = frequencies) => {
    const kids = subcategories[parentKey] || [];
    let total = 0;
    kids.forEach(ch => {
      const rawAmt = subSnap[parentKey]?.[ch.value] ?? '';
      const freq = freqSnap[ch.value] || { count: '1', unit: 'months' };
      total += toMonthly(rawAmt, freq);
    });
    return total;
  };

  const recomputeParentFromChildren = (parentKey, subSnap = subAmounts, freqSnap = frequencies) => {
    const kids = subcategories[parentKey] || [];
    if (!kids.length) return;
    const parentFreq = freqSnap[parentKey] || { count: '1', unit: 'months' };
    const monthly = childrenMonthlyTotal(parentKey, subSnap, freqSnap);
    const parentAmount = fromMonthly(monthly, parentFreq);
    setAmounts(prev => ({ ...prev, [parentKey]: parentAmount ? String(Number(parentAmount.toFixed(2))) : (monthly === 0 ? '' : '0') }));
  };

  const splitsMonthlyTotal = (key, listSnap = null) => {
    const list = listSnap ?? (splits[key] || []);
    if (!list.length) return 0;
    return list.reduce((sum, row) => sum + toMonthly(row.amount, row.freq || { count: '1', unit: 'months' }), 0);
  };

  const recomputeFromSplits = (key, listSnap = null, freqSnap = frequencies) => {
    const monthly = splitsMonthlyTotal(key, listSnap);
    const freq = freqSnap[key] || { count: '1', unit: 'months' };
    const amt = fromMonthly(monthly, freq);
    setAmounts(prev => ({ ...prev, [key]: amt ? String(Number(amt.toFixed(2))) : (monthly === 0 ? '' : '0') }));
  };

  const getBudgetMonths = () => {
    if (!startDate) return [];
    const start = new Date(startDate + 'T12:00:00');
    const end = endDate ? new Date(endDate + 'T12:00:00') : new Date(start);
    if (!endDate) end.setFullYear(start.getFullYear() + 1);
    const months = [];
    let curr = new Date(start.getFullYear(), start.getMonth(), 1);
    const stop = new Date(end.getFullYear(), end.getMonth(), 1);
    while (curr <= stop) {
      const y = curr.getFullYear();
      const m = String(curr.getMonth() + 1).padStart(2, '0');
      months.push(`${y}-${m}`);
      curr.setMonth(curr.getMonth() + 1);
      if (months.length > 60) break;
    }
    return months;
  };

  const computeMonthlyTotal = () => {
    const months = getBudgetMonths();
    return (categories || []).reduce((sum, [val]) => {
      const key = String(val);
      if (useMonthlyOverrides[key] && months.length > 0) {
        let catSum = 0;
        months.forEach(m => { catSum += parseFloat(monthlyOverrides[key]?.[m] || 0); });
        return sum + (catSum / months.length);
      }
      const amt = amounts[key] ?? '';
      const freq = frequencies[key] || { count: '1', unit: 'months' };
      return sum + toMonthly(amt, freq);
    }, 0);
  };

  const debtPaymentMonthlyTotal = () => {
    return Object.entries(debtAmounts).reduce((sum, [key, val]) => {
      const freq = debtFrequencies[key] || { count: '1', unit: 'months' };
      return sum + toMonthly(val, freq);
    }, 0);
  };

  // ─── Asset aggregation helpers ───────────────────────────────────────────────

  // Aggregate per-asset amounts for a parent slug into subAmounts and parent amount
  // assetSnap: { [parentSlug]: { [assetId]: { [childSlug]: amountStr, __freq__<slug>: {count,unit} } } }
  // For leaf asset parents (e.g. landscaping), amounts are stored under '__self__'.
  const recomputeParentFromAssets = (parentKey, childList, assetSnap, freqSnap) => {
    const parentData = (assetSnap || assetAmounts)[parentKey] || {};
    const isLeaf = LEAF_ASSET_PARENTS.includes(parentKey);

    if (isLeaf) {
      // Sum per-property amounts directly to the parent category total
      let totalMonthly = 0;
      Object.values(parentData).forEach(assetMap => {
        const mode = assetMap['__mode____self__'] || 'period';
        if (mode === 'fixed') {
          const overrides = assetMap['__overrides____self__'] || {};
          const vals = Object.values(overrides).map(v => parseFloat(v) || 0);
          const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
          totalMonthly += avg;
        } else {
          const freq = assetMap['__freq____self__'] || freqSnap?.[parentKey] || { count: '1', unit: 'months' };
          totalMonthly += toMonthly(assetMap['__self__'] || 0, freq);
        }
      });
      const parentFreq = freqSnap?.[parentKey] || frequencies[parentKey] || { count: '1', unit: 'months' };
      const parentAmt = fromMonthly(totalMonthly, parentFreq);
      setAmounts(prev => ({ ...prev, [parentKey]: parentAmt ? String(Number(parentAmt.toFixed(2))) : (totalMonthly === 0 ? '' : '0') }));
      return;
    }

    const childTotals = {};
    childList.forEach(ch => {
      let totalMonthly = 0;
      Object.values(parentData).forEach(childMap => {
        const mode = childMap[`__mode__${ch.value}`] || 'period';
        if (mode === 'fixed') {
          // Fixed: average the monthly overrides
          const overrides = childMap[`__overrides__${ch.value}`] || {};
          const vals = Object.values(overrides).map(v => parseFloat(v) || 0);
          const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
          totalMonthly += avg;
        } else {
          // Period: normalize raw amount through stored frequency
          const freq = childMap[`__freq__${ch.value}`] || freqSnap?.[ch.value] || { count: '1', unit: 'months' };
          totalMonthly += toMonthly(childMap[ch.value] || 0, freq);
        }
      });
      childTotals[ch.value] = String(Number(totalMonthly.toFixed(2)));
    });
    let nextSub = null;
    setSubAmounts(prev => {
      nextSub = { ...prev, [parentKey]: { ...(prev[parentKey] || {}), ...childTotals } };
      return nextSub;
    });
    Promise.resolve().then(() => recomputeParentFromChildren(parentKey, nextSub, freqSnap || frequencies));
  };

  // ── Data Loading ──

  const loadBudget = async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`/api/income_statement/budgets/${id}/`);
      setBudget(res.data);
    } catch (e) {
      setError('Failed to load budget.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadBudget(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load physical assets for per-asset hierarchy
  useEffect(() => {
    axios.get('/api/income_statement/budgets/physical-assets/')
      .then(res => setPhysicalAssets(res.data || { vehicles: [], properties: [] }))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-initialize form when budget loads
  useEffect(() => { if (budget) initEditForm(budget); }, [budget]); // eslint-disable-line react-hooks/exhaustive-deps

  const ensureCategories = async () => {
    if (categories && categories.length > 0) return { categories, subcategories };
    try {
      const res = await axios.get('/api/balance_sheet/financing/revolvers/expense-category-choices/');
      const raw = res.data || [];
      const excluded = new Set(['transfer', 'rewards_cashback', 'tv_cellphones', 'income_nonrecurring', 'income_recurring']);
      const parentToChildren = {};
      const childValues = new Set();
      (Array.isArray(raw) ? raw : []).forEach((entry) => {
        if (entry && typeof entry === 'object' && Array.isArray(entry.children) && entry.children.length) {
          const parentVal = String(entry.value ?? '');
          parentToChildren[parentVal] = entry.children.map(ch => ({ value: String(ch.value), label: String(ch.label ?? ch.value) }));
          entry.children.forEach(ch => childValues.add(String(ch.value)));
        }
      });
      const normalized = (Array.isArray(raw) ? raw : []).map((entry) => {
        if (Array.isArray(entry)) { const [v, l] = entry; return [String(v), String(l ?? v ?? '')]; }
        if (entry && typeof entry === 'object') { const v = entry.value ?? Object.keys(entry)[0]; const l = entry.label ?? (v ?? ''); return [String(v), String(l)]; }
        const s = String(entry); return [s, s];
      });
      let filtered = (normalized || []).filter(([v]) => !excluded.has(String(v)) && !childValues.has(String(v)));
      filtered = filtered.sort((a, b) => (String(a[1] || '').localeCompare(String(b[1] || ''))));
      setCategories(filtered);
      setSubcategories(parentToChildren);
      return { categories: filtered, subcategories: parentToChildren };
    } catch (e) {
      const fallback = [['none', 'Uncategorized']];
      setCategories(fallback);
      setSubcategories({});
      return { categories: fallback, subcategories: {} };
    }
  };

  const initEditForm = async (b) => {
    const ensured = await ensureCategories();
    const catList = ensured?.categories || categories;
    const subMap = ensured?.subcategories || subcategories;

    setBudgetName(b.name || '');
    setStartDate(b.start_date || '');
    setEndDate(b.end_date || '');
    setSplits({});

    const init = {};
    const initSubs = {};
    const source = b.category_amounts || {};
    const excluded = new Set(['transfer', 'rewards_cashback', 'income_nonrecurring', 'income_recurring']);

    (catList || []).forEach(([val]) => {
      const key = String(val);
      const children = subMap[key] || [];
      if (children.length) {
        initSubs[key] = {};
        let rawSum = 0;
        let anyChildSet = false;
        children.forEach((child) => {
          const cv = child.value;
          const raw = source.hasOwnProperty(cv) ? source[cv] : '';
          const str = raw === 0 ? '' : (raw !== undefined ? String(raw) : '');
          initSubs[key][cv] = str;
          const num = parseFloat(str);
          if (Number.isFinite(num)) { rawSum += num; anyChildSet = true; }
        });
        if (anyChildSet) {
          const monthlyMap = b.monthly_category_amounts || {};
          let monthlySum = 0;
          children.forEach((child) => { const mv = parseFloat(monthlyMap[String(child.value)]); if (Number.isFinite(mv)) monthlySum += mv; });
          const base = Number.isFinite(monthlySum) && monthlySum > 0 ? monthlySum : rawSum;
          init[key] = base === 0 ? '' : String(Number(base.toFixed(2)));
        } else {
          const pv = source.hasOwnProperty(key) ? source[key] : '';
          init[key] = pv === 0 ? '' : (pv !== undefined ? String(pv) : '');
        }
      } else {
        const v = source.hasOwnProperty(key) ? source[key] : '';
        init[key] = v === 0 ? '' : (v !== undefined ? String(v) : '');
      }
    });
    Object.keys(source).forEach(k => {
      if (excluded.has(String(k))) return;
      const isKnownParent = (catList || []).some(([v]) => String(v) === String(k));
      const isKnownChild = Object.values(subMap || {}).some(arr => (arr || []).some(ch => String(ch.value) === String(k)));
      if (!isKnownParent && !isKnownChild && !(k in init)) { init[k] = String(source[k]); }
    });

    setAmounts(init);
    setSubAmounts(initSubs);
    setMonthlyOverrides(b.category_monthly_overrides || {});
    const overridesMeta = {};
    if (b.category_monthly_overrides) {
      Object.keys(b.category_monthly_overrides).forEach(k => {
        if (Object.keys(b.category_monthly_overrides[k]).length > 0) overridesMeta[k] = true;
      });
    }
    setUseMonthlyOverrides(overridesMeta);

    const fInit = {};
    const srcFreq = b.category_frequencies || {};
    const allowed = new Set(['days', 'weeks', 'months', 'years']);
    (catList || []).forEach(([val]) => {
      const key = String(val);
      const raw = srcFreq[key] || {};
      let count = parseInt(raw.count, 10);
      if (!Number.isFinite(count) || count <= 0) count = 1;
      let unit = raw.unit || 'months';
      unit = allowed.has(unit) ? unit : 'months';
      fInit[key] = { count: String(count), unit };
      const children = (subMap[key] || []);
      children.forEach(ch => {
        const craw = srcFreq[ch.value] || {};
        let ccount = parseInt(craw.count, 10);
        if (!Number.isFinite(ccount) || ccount <= 0) ccount = 1;
        let cunit = craw.unit || 'months';
        cunit = allowed.has(cunit) ? cunit : 'months';
        fInit[ch.value] = { count: String(ccount), unit: cunit };
      });
    });
    Object.keys(srcFreq).forEach((k) => {
      if (!(k in fInit) && !excluded.has(String(k))) {
        const raw = srcFreq[k] || {};
        let count = parseInt(raw.count, 10);
        if (!Number.isFinite(count) || count <= 0) count = 1;
        let unit = raw.unit || 'months';
        unit = allowed.has(unit) ? unit : 'months';
        fInit[k] = { count: String(count), unit };
      }
    });
    setFrequencies(fInit);

    const schedule = b.debt_payment_schedule || {};
    const dAmounts = {};
    const dFreqs = {};
    const dDays = {};
    Object.entries(schedule).forEach(([slugKey, meta]) => {
      const rawAmt = (b.category_amounts || {})[slugKey];
      dAmounts[slugKey] = rawAmt !== undefined && rawAmt !== 0 ? String(rawAmt) : '';
      const rawFreq = (b.category_frequencies || {})[slugKey] || {};
      dFreqs[slugKey] = { count: String(rawFreq.count || '1'), unit: rawFreq.unit || 'months' };
      dDays[slugKey] = meta.payment_day !== undefined && meta.payment_day !== null ? String(meta.payment_day) : '';
    });
    // Per-asset amounts — new structure: { [parentSlug]: { [assetId]: { [childSlug]: amount } } }
    const savedAssetAmounts = b.asset_category_amounts || {};
    setAssetAmounts(savedAssetAmounts);
    // Activate per-asset mode for any parent slug that has saved per-asset data
    const parentModeInit = {};
    const savedIdsInit = {};
    Object.entries(savedAssetAmounts).forEach(([parentSlug, assetMap]) => {
      if (assetMap && Object.keys(assetMap).length > 0) {
        parentModeInit[parentSlug] = 'per_asset';
        savedIdsInit[parentSlug] = Object.keys(assetMap).map(String);
      }
    });
    setParentAssetMode(parentModeInit);
    setSavedAssetIds(savedIdsInit);

    setDebtAmounts(dAmounts);
    setDebtFrequencies(dFreqs);
    setDebtPaymentDays(dDays);
    if (Object.keys(schedule).length > 0) {
      setDebtPaymentExpanded(true);
      axios.get('/api/income_statement/budgets/eligible-debts/').then(res => setEligibleDebts(res.data || [])).catch(() => {});
    } else {
      setDebtPaymentExpanded(false);
      setEligibleDebts([]);
    }

    setTimeout(() => {
      (Object.entries(subMap) || []).forEach(([p, kids]) => {
        if ((kids || []).length) recomputeParentFromChildren(String(p), initSubs, fInit);
      });
    }, 0);

  };


  // ── Amount / Frequency change handlers ──

  const onChangeAmount = (key, value) => {
    const hasChildren = Array.isArray(subcategories[key]) && subcategories[key].length > 0;
    if (hasChildren) {
      const children = subcategories[key] || [];
      const other = children.find(ch => ch.value === `${key}__other`) || null;
      const num = parseFloat(value);
      setSubAmounts(prev => {
        const next = { ...prev };
        next[key] = next[key] ? { ...next[key] } : {};
        children.forEach(ch => { next[key][ch.value] = '0'; });
        if (other) next[key][other.value] = Number.isFinite(num) ? String(num) : '';
        return next;
      });
      if (other) {
        setFrequencies(prev => {
          const pf = prev[key] || { count: '1', unit: 'months' };
          return { ...prev, [other.value]: { count: String(pf.count ?? '1'), unit: pf.unit || 'months' } };
        });
      }
      setAmounts(prev => ({ ...prev, [key]: value }));
      return;
    }
    setAmounts(prev => ({ ...prev, [key]: value }));
  };

  const onChangeChildAmount = (parent, childValue, value) => {
    let nextSub = null;
    setSubAmounts(prev => {
      nextSub = { ...prev };
      nextSub[parent] = nextSub[parent] ? { ...nextSub[parent] } : {};
      nextSub[parent][childValue] = value;
      return nextSub;
    });
    Promise.resolve().then(() => recomputeParentFromChildren(parent, nextSub, frequencies));
  };

  const onChangeDebtAmount = (key, value) => {
    const nextDebtAmounts = { ...debtAmounts, [key]: value };
    setDebtAmounts(nextDebtAmounts);
    const monthly = Object.entries(nextDebtAmounts).reduce((sum, [k, v]) => {
      const freq = debtFrequencies[k] || { count: '1', unit: 'months' };
      return sum + toMonthly(v, freq);
    }, 0);
    const parentFreq = frequencies['payments'] || { count: '1', unit: 'months' };
    const parentAmt = fromMonthly(monthly, parentFreq);
    setAmounts(prev => ({ ...prev, payments: parentAmt ? String(Number(parentAmt.toFixed(2))) : (monthly === 0 ? '' : '0') }));
  };

  const onChangeDebtFrequency = (key, field, value) => {
    const nextFreqs = { ...debtFrequencies, [key]: { ...(debtFrequencies[key] || {}), [field]: value } };
    setDebtFrequencies(nextFreqs);
    const monthly = Object.entries(debtAmounts).reduce((sum, [k, v]) => {
      const freq = nextFreqs[k] || { count: '1', unit: 'months' };
      return sum + toMonthly(v, freq);
    }, 0);
    const parentFreq = frequencies['payments'] || { count: '1', unit: 'months' };
    const parentAmt = fromMonthly(monthly, parentFreq);
    setAmounts(prev => ({ ...prev, payments: parentAmt ? String(Number(parentAmt.toFixed(2))) : (monthly === 0 ? '' : '0') }));
  };

  const toggleDebtPaymentExpand = async () => {
    if (!debtPaymentExpanded && eligibleDebts.length === 0) {
      setLoadingDebts(true);
      try {
        const res = await axios.get('/api/income_statement/budgets/eligible-debts/');
        const debts = res.data || [];
        setEligibleDebts(debts);
        setDebtAmounts(prev => { const next = { ...prev }; debts.forEach(d => { const k = `payments__debt_${d.id}`; if (!(k in next)) next[k] = ''; }); return next; });
        setDebtFrequencies(prev => { const next = { ...prev }; debts.forEach(d => { const k = `payments__debt_${d.id}`; if (!(k in next)) next[k] = { count: '1', unit: 'months' }; }); return next; });
        setDebtPaymentDays(prev => { const next = { ...prev }; debts.forEach(d => { const k = `payments__debt_${d.id}`; if (!(k in next)) next[k] = ''; }); return next; });
      } catch (e) { /* ignore */ }
      finally { setLoadingDebts(false); }
    }
    setDebtPaymentExpanded(prev => !prev);
  };

  const toggleExpand = (parent) => {
    setExpandedParents(prev => {
      const next = new Set(Array.from(prev));
      if (next.has(parent)) next.delete(parent); else next.add(parent);
      return next;
    });
  };

  // ── Save helpers ──

  // Builds the full budget payload and PATCHes to the API.
  // overrideAssetAmounts: pass the latest assetAmounts when calling from inside a state-update
  // callback (React state may not yet reflect the new value).
  const buildAndPatchBudget = async (overrideAssetAmounts) => {
    if (!startDate) { setSaveError('Start date is required.'); return; }
    const payloadMap = {};
    Object.entries(amounts || {}).forEach(([k, v]) => { const num = parseFloat(v); payloadMap[k] = Number.isFinite(num) ? num : 0; });
    Object.entries(subAmounts || {}).forEach(([parent, childMap]) => {
      const children = subcategories[parent] || [];
      if (!children.length) return;
      let monthlySum = 0;
      children.forEach((ch) => {
        const raw = (childMap || {})[ch.value];
        const num = parseFloat(raw);
        const val = Number.isFinite(num) ? num : 0;
        payloadMap[ch.value] = val;
        const cf = frequencies[ch.value] || { count: '1', unit: 'months' };
        monthlySum += toMonthly(val, cf);
      });
      const pf = frequencies[parent] || { count: '1', unit: 'months' };
      const parentVal = fromMonthly(monthlySum, pf);
      payloadMap[parent] = Number.isFinite(parentVal) ? Number(parentVal.toFixed(2)) : 0;
    });

    const freqMap = {};
    const allowed = new Set(['days', 'weeks', 'months', 'years']);
    const keys = new Set([...Object.keys(amounts || {}), ...Object.keys(frequencies || {}), ...Object.keys(monthlyOverrides || {})]);
    keys.forEach((k) => {
      const raw = (frequencies || {})[k] || {};
      let count = parseInt(raw.count, 10);
      if (!Number.isFinite(count) || count <= 0) count = 1;
      let unit = (raw.unit || 'months');
      unit = allowed.has(unit) ? unit : 'months';
      freqMap[k] = { count, unit };
    });

    const overridesMap = {};
    Object.entries(useMonthlyOverrides || {}).forEach(([k, use]) => {
      if (use && monthlyOverrides[k]) {
        const catOverrides = {};
        Object.entries(monthlyOverrides[k]).forEach(([month, val]) => {
          const num = parseFloat(val);
          if (Number.isFinite(num)) catOverrides[month] = num;
        });
        if (Object.keys(catOverrides).length > 0) overridesMap[k] = catOverrides;
      }
    });

    Object.entries(debtAmounts).forEach(([k, v]) => { const num = parseFloat(v); payloadMap[k] = Number.isFinite(num) ? num : 0; });
    Object.entries(debtFrequencies).forEach(([k, freq]) => {
      let count = parseInt(freq.count, 10);
      if (!Number.isFinite(count) || count <= 0) count = 1;
      let unit = freq.unit || 'months';
      if (!allowed.has(unit)) unit = 'months';
      freqMap[k] = { count, unit };
    });

    const debtSchedule = {};
    eligibleDebts.forEach(d => {
      const k = `payments__debt_${d.id}`;
      const amt = parseFloat(debtAmounts[k]);
      if (Number.isFinite(amt) && amt > 0) {
        debtSchedule[k] = { debt_id: d.id, debt_type: d.debt_type, debt_name: d.name, payment_day: debtPaymentDays[k] ? parseInt(debtPaymentDays[k], 10) : null };
      }
    });

    const monthly_total = computeMonthlyTotal();
    const effectiveAssetAmounts = overrideAssetAmounts !== undefined ? overrideAssetAmounts : assetAmounts;
    const assetCategoryAmounts = {};
    Object.entries(parentAssetMode).forEach(([parentSlug, mode]) => {
      if (mode !== 'per_asset') return;
      const parentData = effectiveAssetAmounts[parentSlug] || {};
      const cleanedParent = {};
      Object.entries(parentData).forEach(([aid, childMap]) => {
        const cleanedChild = {};
        Object.entries(childMap || {}).forEach(([chSlug, v]) => {
          if (chSlug.startsWith('__mode__')) {
            cleanedChild[chSlug] = v;
          } else if (chSlug.startsWith('__overrides__')) {
            const cleanedOverrides = {};
            Object.entries(v || {}).forEach(([month, ov]) => {
              const num = parseFloat(ov);
              cleanedOverrides[month] = Number.isFinite(num) ? num : 0;
            });
            cleanedChild[chSlug] = cleanedOverrides;
          } else if (chSlug.startsWith('__freq__')) {
            cleanedChild[chSlug] = v;
          } else {
            const num = parseFloat(v);
            cleanedChild[chSlug] = Number.isFinite(num) ? num : 0;
          }
        });
        cleanedParent[aid] = cleanedChild;
      });
      if (Object.keys(cleanedParent).length > 0) assetCategoryAmounts[parentSlug] = cleanedParent;
    });

    const payload = {
      name: budgetName || null,
      start_date: startDate,
      category_amounts: payloadMap,
      category_frequencies: freqMap,
      category_monthly_overrides: overridesMap,
      monthly_total: Number.isFinite(monthly_total) ? Number(monthly_total.toFixed(2)) : 0,
      debt_payment_schedule: debtSchedule,
      asset_category_amounts: assetCategoryAmounts,
    };
    if (endDate) payload.end_date = endDate; else payload.end_date = null;

    const res = await axios.patch(`/api/income_statement/budgets/${id}/`, payload);
    setBudget(res.data);
  };

  const handleSave = async () => {
    setSaving(true); setSaveError('');
    try {
      await buildAndPatchBudget(assetAmounts);
    } catch (e) {
      const msg = e.response?.data || e.message;
      setSaveError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this budget? This cannot be undone.')) return;
    try {
      await axios.delete(`/api/income_statement/budgets/${id}/`);
      navigate('/income_statement/budgets');
    } catch (e) {
      setSaveError('Failed to delete budget.');
    }
  };

  // ── Render helpers ──

  const fmt = (num) => {
    if (num === null || num === undefined) return '—';
    const n = Number(num);
    if (!Number.isFinite(n)) return '—';
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // ── Form ──

  const renderEditForm = () => {
    return (
      <div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16, alignItems: 'flex-end' }}>
          <div style={{ minWidth: 240 }}>
            <label className="control-label">Budget Name</label><br />
            <input type="text" className="control-select" value={budgetName} onChange={e => setBudgetName(e.target.value)} placeholder="e.g., 2025 Family Budget" />
          </div>
          <div style={{ minWidth: 180 }}>
            <label className="control-label">Start Date</label><br />
            <input type="date" className="control-select" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div style={{ minWidth: 180 }}>
            <label className="control-label">End Date (optional)</label><br />
            <input type="date" className="control-select" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: '#34d399', alignSelf: 'flex-end', paddingBottom: 4 }}>
            Total: {fmt(computeMonthlyTotal())}/mo
          </div>
        </div>

        <div style={{ fontWeight: 600, marginBottom: 8, color: '#e5e7eb' }}>Expense Categories</div>
        <div style={{ border: '1px solid #374151', borderRadius: 6 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 0.8fr 0.6fr 0.8fr 80px', gap: 8, padding: '8px 12px', position: 'sticky', top: 0, background: '#111827', borderBottom: '1px solid #374151', color: '#9ca3af', fontSize: 13 }}>
            <div>Category</div><div>Amount ($)</div><div>Every</div><div>Frequency</div><div>Mode</div>
          </div>
          <div>
            {(categories || []).map(([val, label]) => {
              const key = String(val);
              const isDebtPayment = key === 'payments';
              const children = subcategories[key] || [];
              const hasChildren = children.length > 0;
              const expanded = expandedParents.has(key);
              return (
                <React.Fragment key={key}>
                  {(() => {
                    const isAssetParent = key === 'automotive' || key === 'home' || key === 'landscaping' || key === 'utilities' || key === 'waste_management';
                    const curAssetMode = parentAssetMode[key] || 'total';
                    const inPerAsset = isAssetParent && curAssetMode === 'per_asset';
                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 0.8fr 0.6fr 0.8fr 80px', gap: 8, alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #111827' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#e5e7eb' }}>
                          {isDebtPayment ? (
                            <button onClick={toggleDebtPaymentExpand} className="btn btn-secondary" style={{ width: 22, height: 22, borderRadius: 9999, fontSize: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                              {debtPaymentExpanded ? '−' : '+'}
                            </button>
                          ) : hasChildren && !inPerAsset ? (
                            <button onClick={() => toggleExpand(key)} className="btn btn-secondary" style={{ width: 22, height: 22, borderRadius: 9999, fontSize: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                              {expanded ? '−' : '+'}
                            </button>
                          ) : (
                            <span style={{ width: 22, height: 22, display: 'inline-block' }} />
                          )}
                          <span>{label}</span>
                        </div>
                        <div>
                          {!inPerAsset && !useMonthlyOverrides[key] ? (
                            <input type="number" step="0.01" value={amounts[key] ?? ''} onChange={e => onChangeAmount(key, e.target.value)} className="control-select" style={{ width: '100%' }} placeholder="0.00"
                              disabled={(isDebtPayment && debtPaymentExpanded && eligibleDebts.length > 0) || (!hasChildren && Boolean((splits[key] || []).length))} />
                          ) : inPerAsset ? (() => {
                            const isLeafParent = LEAF_ASSET_PARENTS.includes(key);
                            const children2 = subcategories[key] || [];
                            const savedIds2 = savedAssetIds[key] || [];
                            // Build maps to aggregate: all saved assets + current draft if mid-entry
                            const mapsToAgg2 = savedIds2.map(aid => (assetAmounts[key] || {})[aid] || {});
                            if (addingAssetFor === key && selectedNewAssetId) {
                              const draftMap2 = { ...draftAssetAmounts };
                              if (isLeafParent) {
                                draftMap2['__mode____self__'] = draftAssetChildMode['__self__'] || 'period';
                                draftMap2['__overrides____self__'] = draftAssetMonthlyOverrides['__self__'] || {};
                                const freq = frequencies[key] || { count: '1', unit: 'months' };
                                draftMap2['__freq____self__'] = { count: freq.count, unit: freq.unit };
                              } else {
                                Object.entries(draftAssetChildMode).forEach(([slug, mode]) => { draftMap2[`__mode__${slug}`] = mode; });
                                Object.entries(draftAssetMonthlyOverrides).forEach(([slug, ovr]) => { draftMap2[`__overrides__${slug}`] = ovr; });
                                children2.forEach(ch => {
                                  const freq = frequencies[ch.value] || { count: '1', unit: 'months' };
                                  draftMap2[`__freq__${ch.value}`] = { count: freq.count, unit: freq.unit };
                                });
                              }
                              mapsToAgg2.push(draftMap2);
                            }
                            const budgetMonths2 = getBudgetMonths();
                            // Sum each month correctly: Fixed = only that month's override; Period = toMonthly per stored freq
                            const annualTotal = budgetMonths2.reduce((yearSum, month) => {
                              const monthTotal = mapsToAgg2.reduce((sum, assetMap) => {
                                if (isLeafParent) {
                                  const mode = assetMap['__mode____self__'] || 'period';
                                  if (mode === 'fixed') {
                                    const overrides = assetMap['__overrides____self__'] || {};
                                    return sum + (parseFloat(overrides[month] || 0) || 0);
                                  }
                                  const freq = assetMap['__freq____self__'] || frequencies[key] || { count: '1', unit: 'months' };
                                  const rawAmt = parseFloat(assetMap['__self__'] || 0) || 0;
                                  const cnt = Math.max(1, parseInt(freq?.count, 10) || 1);
                                  const unit = freq?.unit || 'months';
                                  const mo = unit === 'days' ? rawAmt * (30 / cnt) : unit === 'weeks' ? rawAmt * (4 / cnt) : unit === 'years' ? rawAmt / (12 * cnt) : rawAmt / cnt;
                                  return sum + mo;
                                }
                                const childSum = children2.reduce((s, ch) => {
                                  const mode = assetMap[`__mode__${ch.value}`] || 'period';
                                  if (mode === 'fixed') {
                                    const overrides = assetMap[`__overrides__${ch.value}`] || {};
                                    return s + (parseFloat(overrides[month] || 0) || 0);
                                  }
                                  const freq = assetMap[`__freq__${ch.value}`] || frequencies[ch.value] || { count: '1', unit: 'months' };
                                  const rawAmt = parseFloat(assetMap[ch.value] || 0) || 0;
                                  const cnt = Math.max(1, parseInt(freq?.count, 10) || 1);
                                  const unit = freq?.unit || 'months';
                                  const monthlyAmt = unit === 'days' ? rawAmt * (30 / cnt) : unit === 'weeks' ? rawAmt * (4 / cnt) : unit === 'years' ? rawAmt / (12 * cnt) : rawAmt / cnt;
                                  return s + monthlyAmt;
                                }, 0);
                                return sum + childSum;
                              }, 0);
                              return yearSum + monthTotal;
                            }, 0);
                            const avgMonthly2 = annualTotal / 12;
                            return (
                              <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'right' }}>
                                {mapsToAgg2.length === 0 ? '—' : (
                                  <span title="Average monthly budget (sum of all monthly aggregates ÷ 12)">
                                    {fmt(avgMonthly2)}<span style={{ fontSize: 10, color: '#6b7280', marginLeft: 3 }}>/mo</span>
                                    <span style={{ fontSize: 10, color: '#6b7280', marginLeft: 6 }}>({fmt(annualTotal)}<span style={{ marginLeft: 2 }}>/ yr</span>)</span>
                                  </span>
                                )}
                              </div>
                            );
                          })() : <div style={{ fontSize: 11, color: '#9ca3af' }}>Monthly values below</div>}
                        </div>
                        <div>
                          {!inPerAsset && !useMonthlyOverrides[key] ? (
                            <input type="number" min="1" step="1" value={(frequencies[key]?.count) ?? '1'} onChange={e => {
                              const newVal = e.target.value;
                              let nextFreq = null;
                              setFrequencies(prev => { nextFreq = { ...prev, [key]: { ...(prev[key] || {}), count: newVal } }; return nextFreq; });
                              Promise.resolve().then(() => {
                                if ((subcategories[key] || []).length) recomputeParentFromChildren(key, subAmounts, nextFreq);
                                else if ((splits[key] || []).length) recomputeFromSplits(key, splits[key] || [], nextFreq);
                              });
                            }} className="control-select" style={{ width: '100%' }} placeholder="1" />
                          ) : <div style={{ textAlign: 'center' }}>—</div>}
                        </div>
                        <div>
                          {!inPerAsset && !useMonthlyOverrides[key] ? (
                            <select className="control-select" value={(frequencies[key]?.unit) ?? 'months'} onChange={e => {
                              const newUnit = e.target.value;
                              let nextFreq = null;
                              setFrequencies(prev => { nextFreq = { ...prev, [key]: { ...(prev[key] || {}), unit: newUnit } }; return nextFreq; });
                              Promise.resolve().then(() => {
                                if ((subcategories[key] || []).length) recomputeParentFromChildren(key, subAmounts, nextFreq);
                                else if ((splits[key] || []).length) recomputeFromSplits(key, splits[key] || [], nextFreq);
                              });
                            }} style={{ width: '100%' }}>
                              <option value="days">days</option>
                              <option value="weeks">weeks</option>
                              <option value="months">months</option>
                              <option value="years">years</option>
                            </select>
                          ) : <div style={{ textAlign: 'center' }}>—</div>}
                        </div>
                        <div>
                          {isAssetParent ? (
                            <select className="control-select" style={{ width: '100%', fontSize: 12 }}
                              value={curAssetMode}
                              onChange={e => {
                                const v = e.target.value;
                                setParentAssetMode(prev => ({ ...prev, [key]: v }));
                                if (v === 'total') {
                                  // switching back to total — clear saved asset data for this parent
                                  setAssetAmounts(prev => { const n = { ...prev }; delete n[key]; return n; });
                                  setSavedAssetIds(prev => { const n = { ...prev }; delete n[key]; return n; });
                                  setAddingAssetFor(null);
                                } else {
                                  // switching to per_asset — collapse all other expanded parents so the
                                  // per-asset panel appears immediately below this category row
                                  setExpandedParents(new Set([key]));
                                }
                              }}>
                              <option value="total">Total</option>
                              <option value="per_asset">{key === 'automotive' ? 'Per Vehicle' : 'Per Property'}</option>
                            </select>
                          ) : !hasChildren ? (
                            <select className="control-select" style={{ width: '100%', fontSize: 12 }} value={useMonthlyOverrides[key] ? 'monthly' : 'fixed'} onChange={e => { const v = e.target.value; setUseMonthlyOverrides(prev => ({ ...prev, [key]: v === 'monthly' })); }}>
                              <option value="fixed">Fixed</option>
                              <option value="monthly">Monthly</option>
                            </select>
                          ) : null}
                        </div>
                      </div>
                    );
                  })()}

                  {useMonthlyOverrides[key] && !(parentAssetMode[key] === 'per_asset') && (
                    <div style={{ padding: '8px 12px 12px 40px', background: '#0b1220', borderBottom: '1px solid #111827' }}>
                      <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 8 }}>Monthly Overrides</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 12 }}>
                        {getBudgetMonths().map(month => (
                          <div key={month}>
                            <label style={{ fontSize: 10, display: 'block', marginBottom: 2, color: '#6b7280' }}>{month}</label>
                            <input type="number" step="0.01" className="control-select" style={{ width: '100%', height: 28, fontSize: 12 }}
                              value={monthlyOverrides[key]?.[month] ?? ''} onChange={e => { const v = e.target.value; setMonthlyOverrides(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [month]: v } })); }} placeholder="0.00" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(() => {
                    const isAssetParentMo = key === 'automotive' || key === 'home' || key === 'utilities' || key === 'waste_management' || LEAF_ASSET_PARENTS.includes(key);
                    const inPerAssetMo = isAssetParentMo && (parentAssetMode[key] || 'total') === 'per_asset';
                    if (!inPerAssetMo) return null;
                    const months = getBudgetMonths();
                    if (!months.length) return null;
                    const isLeafMo = LEAF_ASSET_PARENTS.includes(key);
                    const childrenMo = subcategories[key] || [];
                    const savedIdsMo = savedAssetIds[key] || [];

                    // Build a list of asset-amount maps to aggregate: all saved + current draft if editing
                    const assetMapsToAggregate = savedIdsMo.map(aid => (assetAmounts[key] || {})[aid] || {});
                    // Include draft if the user is currently adding/editing an asset for this parent
                    if (addingAssetFor === key && selectedNewAssetId) {
                      // Build a draft map the same way the Save handler does (include freq metadata)
                      const draftMap = { ...draftAssetAmounts };
                      if (isLeafMo) {
                        draftMap['__mode____self__'] = draftAssetChildMode['__self__'] || 'period';
                        draftMap['__overrides____self__'] = draftAssetMonthlyOverrides['__self__'] || {};
                        const freq = frequencies[key] || { count: '1', unit: 'months' };
                        draftMap['__freq____self__'] = { count: freq.count, unit: freq.unit };
                      } else {
                        Object.entries(draftAssetChildMode).forEach(([slug, mode]) => { draftMap[`__mode__${slug}`] = mode; });
                        Object.entries(draftAssetMonthlyOverrides).forEach(([slug, overrides]) => { draftMap[`__overrides__${slug}`] = overrides; });
                        childrenMo.forEach(ch => {
                          const freq = frequencies[ch.value] || { count: '1', unit: 'months' };
                          draftMap[`__freq__${ch.value}`] = { count: freq.count, unit: freq.unit };
                        });
                      }
                      assetMapsToAggregate.push(draftMap);
                    }

                    if (assetMapsToAggregate.length === 0) return null;

                    // Compute per-month aggregate
                    // Fixed mode: only the specific month's override value (0 in other months)
                    // Period mode: normalize raw amount via stored frequency (e.g. annual/12 each month)
                    const monthTotals = months.map(month => {
                      const total = assetMapsToAggregate.reduce((sum, assetMap) => {
                        if (isLeafMo) {
                          const mode = assetMap['__mode____self__'] || 'period';
                          if (mode === 'fixed') {
                            const overrides = assetMap['__overrides____self__'] || {};
                            return sum + (parseFloat(overrides[month] || 0) || 0);
                          }
                          const freq = assetMap['__freq____self__'] || frequencies[key] || { count: '1', unit: 'months' };
                          const rawAmt = parseFloat(assetMap['__self__'] || 0) || 0;
                          const cnt = Math.max(1, parseInt(freq?.count, 10) || 1);
                          const unit = freq?.unit || 'months';
                          const mo = unit === 'days' ? rawAmt * (30 / cnt) : unit === 'weeks' ? rawAmt * (4 / cnt) : unit === 'years' ? rawAmt / (12 * cnt) : rawAmt / cnt;
                          return sum + mo;
                        }
                        const childSum = childrenMo.reduce((s, ch) => {
                          const mode = assetMap[`__mode__${ch.value}`] || 'period';
                          if (mode === 'fixed') {
                            // Use only this month's specific override — do NOT average across months
                            const overrides = assetMap[`__overrides__${ch.value}`] || {};
                            return s + (parseFloat(overrides[month] || 0) || 0);
                          }
                          // Period: convert raw entered amount to monthly via stored frequency
                          const freq = assetMap[`__freq__${ch.value}`] || frequencies[ch.value] || { count: '1', unit: 'months' };
                          const rawAmt = parseFloat(assetMap[ch.value] || 0) || 0;
                          const monthlyAmt = (() => {
                            const cnt = Math.max(1, parseInt(freq?.count, 10) || 1);
                            const unit = freq?.unit || 'months';
                            switch (unit) {
                              case 'days': return rawAmt * (30 / cnt);
                              case 'weeks': return rawAmt * (4 / cnt);
                              case 'months': return rawAmt * (1 / cnt);
                              case 'years': return rawAmt * (1 / (12 * cnt));
                              default: return rawAmt * (1 / cnt);
                            }
                          })();
                          return s + monthlyAmt;
                        }, 0);
                        return sum + childSum;
                      }, 0);
                      return { month, total };
                    });

                    return (
                      <div style={{ padding: '8px 12px 12px 40px', background: '#060d17', borderBottom: '1px solid #111827' }}>
                        <div style={{ fontSize: 12, color: '#60a5fa', marginBottom: 8 }}>
                          Monthly Aggregate ({key === 'automotive' ? 'all vehicles' : 'all properties'})
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 12 }}>
                          {monthTotals.map(({ month, total }) => (
                            <div key={month}>
                              <label style={{ fontSize: 10, display: 'block', marginBottom: 2, color: '#6b7280' }}>{month}</label>
                              <div style={{ fontSize: 13, color: total > 0 ? '#34d399' : '#4b5563', fontWeight: 600, padding: '3px 6px', background: '#0b1220', borderRadius: 4, border: '1px solid #1f2937' }}>
                                {total > 0 ? `$${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {isDebtPayment && debtPaymentExpanded && (
                    <div style={{ padding: '0 12px 8px 12px', background: '#0b1220', borderBottom: '1px solid #111827' }}>
                      {loadingDebts && <div style={{ color: '#9ca3af', padding: '8px 0', fontSize: 13 }}>Loading debt obligations…</div>}
                      {!loadingDebts && eligibleDebts.length === 0 && <div style={{ color: '#9ca3af', padding: '8px 0', fontSize: 13 }}>No eligible debts found.</div>}
                      {!loadingDebts && eligibleDebts.length > 0 && (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.8fr 0.5fr 0.7fr 80px', gap: 8, padding: '6px 0 4px 0', fontSize: 11, color: '#6b7280', borderBottom: '1px solid #1f2937' }}>
                            <div>Debt Obligation</div><div>Payment Amount ($)</div><div>Every</div><div>Frequency</div><div>Pay Day</div>
                          </div>
                          {eligibleDebts.map(debt => {
                            const dk = `payments__debt_${debt.id}`;
                            return (
                              <div key={dk} style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.8fr 0.5fr 0.7fr 80px', gap: 8, alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #111827' }}>
                                <div style={{ color: '#d1d5db', fontSize: 13 }}>↳ {debt.name} <span style={{ color: '#6b7280', fontSize: 11 }}>({debt.debt_type.replace('_', ' ')})</span></div>
                                <div><input type="number" step="0.01" value={debtAmounts[dk] ?? ''} onChange={e => onChangeDebtAmount(dk, e.target.value)} className="control-select" style={{ width: '100%' }} placeholder="0.00" /></div>
                                <div><input type="number" min="1" step="1" value={debtFrequencies[dk]?.count ?? '1'} onChange={e => onChangeDebtFrequency(dk, 'count', e.target.value)} className="control-select" style={{ width: '100%' }} placeholder="1" /></div>
                                <div>
                                  <select className="control-select" style={{ width: '100%' }} value={debtFrequencies[dk]?.unit ?? 'months'} onChange={e => onChangeDebtFrequency(dk, 'unit', e.target.value)}>
                                    <option value="days">days</option><option value="weeks">weeks</option><option value="months">months</option><option value="years">years</option>
                                  </select>
                                </div>
                                <div><input type="number" min="1" max="31" step="1" value={debtPaymentDays[dk] ?? ''} onChange={e => setDebtPaymentDays(prev => ({ ...prev, [dk]: e.target.value }))} className="control-select" style={{ width: '100%' }} placeholder="Day #" /></div>
                              </div>
                            );
                          })}
                          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
                            Total Debt Payment (monthly): <strong style={{ color: '#e5e7eb' }}>{fmt(debtPaymentMonthlyTotal())}</strong>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {!isDebtPayment && (hasChildren || LEAF_ASSET_PARENTS.includes(key)) && (() => {
                    const isAssetParent = key === 'automotive' || key === 'home' || key === 'utilities' || key === 'waste_management' || LEAF_ASSET_PARENTS.includes(key);
                    const isLeafAsset = LEAF_ASSET_PARENTS.includes(key);
                    const curAssetMode = parentAssetMode[key] || 'total';
                    const inPerAsset = isAssetParent && curAssetMode === 'per_asset';
                    const assetList = key === 'automotive' ? physicalAssets.vehicles : physicalAssets.properties;
                    const assetLabel = key === 'automotive' ? 'vehicle' : 'property';
                    const assetLabelCap = key === 'automotive' ? 'Vehicle' : 'Property';
                    const savedIds = savedAssetIds[key] || [];

                    if (inPerAsset) {
                      // ── Per-asset panel ──
                      // When editing an existing asset, it stays in savedIds so the card remains visible;
                      // include it in availableAssets so the dropdown shows/selects it properly.
                      const editingAssetId = addingAssetFor === key ? String(selectedNewAssetId) : null;
                      const availableAssets = assetList.filter(a => {
                        const sid = String(a.id);
                        if (editingAssetId && sid === editingAssetId) return true; // show currently-edited asset
                        return !savedIds.includes(sid);
                      });
                      const isAddingThis = addingAssetFor === key;

                      return (
                        <div style={{ padding: '12px', background: '#0b1220', borderBottom: '1px solid #374151' }}>

                          {/* Saved asset cards */}
                          {savedIds.map(aid => {
                            const asset = assetList.find(a => String(a.id) === String(aid));
                            const assetChildAmounts = (assetAmounts[key] || {})[aid] || {};
                            // Compute display total: leaf = single __self__ amount; multi-child = sum of child amounts
                            const displayTotal = isLeafAsset
                              ? (() => {
                                  const mode = assetChildAmounts['__mode____self__'] || 'period';
                                  if (mode === 'fixed') {
                                    const ovr = assetChildAmounts['__overrides____self__'] || {};
                                    const vals = Object.values(ovr).map(v => parseFloat(v) || 0);
                                    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
                                  }
                                  const freq = assetChildAmounts['__freq____self__'] || frequencies[key] || { count: '1', unit: 'months' };
                                  return toMonthly(assetChildAmounts['__self__'] || 0, freq);
                                })()
                              : children.reduce((s, ch) => s + (parseFloat(assetChildAmounts[ch.value] || 0) || 0), 0);
                            const isBeingEdited = editingAssetId === String(aid);
                            return (
                              <div key={aid} style={{ border: `1px solid ${isBeingEdited ? '#3b82f6' : '#374151'}`, borderRadius: 6, marginBottom: 10, overflow: 'hidden', opacity: isBeingEdited ? 0.45 : 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#111827' }}>
                                  <div style={{ color: '#e5e7eb', fontWeight: 600, fontSize: 14 }}>
                                    {asset ? asset.label : `Asset #${aid}`}
                                    <span style={{ marginLeft: 10, fontSize: 12, color: '#34d399', fontWeight: 400 }}>{fmt(displayTotal)}/mo</span>
                                  </div>
                                  <div style={{ display: 'flex', gap: 8 }}>
                                    <button className="btn btn-secondary" style={{ fontSize: 11, padding: '2px 10px' }}
                                      onClick={() => {
                                        // Edit: open draft for editing without removing from savedAssetIds
                                        const existing = (assetAmounts[key] || {})[aid] || {};
                                        if (isLeafAsset) {
                                          setDraftAssetAmounts({ '__self__': String(existing['__self__'] ?? '') });
                                          setDraftAssetChildMode({ '__self__': existing['__mode____self__'] || 'period' });
                                          setDraftAssetMonthlyOverrides({ '__self__': existing['__overrides____self__'] || {} });
                                        } else {
                                          // Restore plain amounts (exclude metadata keys)
                                          const restoredAmounts = Object.fromEntries(
                                            children.map(ch => [ch.value, String(existing[ch.value] ?? '')])
                                          );
                                          // Restore child modes and overrides from metadata keys
                                          const restoredChildMode = {};
                                          const restoredOverrides = {};
                                          children.forEach(ch => {
                                            const mode = existing[`__mode__${ch.value}`];
                                            if (mode) restoredChildMode[ch.value] = mode;
                                            const overrides = existing[`__overrides__${ch.value}`];
                                            if (overrides) restoredOverrides[ch.value] = overrides;
                                          });
                                          setDraftAssetAmounts(restoredAmounts);
                                          setDraftAssetChildMode(restoredChildMode);
                                          setDraftAssetMonthlyOverrides(restoredOverrides);
                                        }
                                        setSelectedNewAssetId(String(aid));
                                        setAddingAssetFor(key);
                                        // Do NOT remove from savedAssetIds here — Cancel will restore view without changes
                                      }}>Edit</button>
                                    <button style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 11 }}
                                      onClick={async () => {
                                        if (!window.confirm(`Remove ${asset ? asset.label : assetLabel} budget?`)) return;
                                        const nextAssetAfterRemove = { ...assetAmounts, [key]: { ...(assetAmounts[key] || {}) } };
                                        delete nextAssetAfterRemove[key][String(aid)];
                                        setSavedAssetIds(prev => ({ ...prev, [key]: (prev[key] || []).filter(i => String(i) !== String(aid)) }));
                                        setAssetAmounts(nextAssetAfterRemove);
                                        recomputeParentFromAssets(key, children, nextAssetAfterRemove, frequencies);
                                        // Auto-save removal to backend
                                        setSaving(true); setSaveError('');
                                        try { await buildAndPatchBudget(nextAssetAfterRemove); }
                                        catch (e) { const msg = e.response?.data || e.message; setSaveError(typeof msg === 'string' ? msg : JSON.stringify(msg)); }
                                        finally { setSaving(false); }
                                      }}>Remove</button>
                                  </div>
                                </div>
                                {!isLeafAsset && (
                                  <div style={{ padding: '8px 12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '6px 16px' }}>
                                    {children.map(ch => {
                                      const chMode = assetChildAmounts[`__mode__${ch.value}`];
                                      const isChFixed = chMode === 'fixed';
                                      return (
                                        <div key={ch.value} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                          <span style={{ color: '#9ca3af' }}>{ch.label}{isChFixed ? <span style={{ fontSize: 10, color: '#60a5fa', marginLeft: 4 }}>Fixed</span> : null}</span>
                                          <span style={{ color: '#e5e7eb' }}>{fmt(parseFloat(assetChildAmounts[ch.value] || 0) || 0)}/mo</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {/* Add new asset form */}
                          {isAddingThis ? (
                            <div style={{ border: '1px solid #3b82f6', borderRadius: 6, padding: 12, background: '#0d1b2e' }}>
                              <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                                <label style={{ color: '#9ca3af', fontSize: 13 }}>{assetLabelCap}:</label>
                                <select className="control-select" style={{ minWidth: 200 }}
                                  value={selectedNewAssetId}
                                  onChange={e => setSelectedNewAssetId(e.target.value)}>
                                  <option value="">— Select {assetLabel} —</option>
                                  {availableAssets.map(a => (
                                    <option key={a.id} value={String(a.id)}>{a.label}</option>
                                  ))}
                                </select>
                              </div>
                              {selectedNewAssetId && (
                                <>
                                  {isLeafAsset ? (
                                    // ── Leaf per-asset form (single amount row, no child categories) ──
                                    (() => {
                                      const isFixed = draftAssetChildMode['__self__'] === 'fixed';
                                      return (
                                        <React.Fragment>
                                          <div style={{ display: 'grid', gridTemplateColumns: '0.7fr 0.5fr 0.7fr 70px', gap: 8, marginBottom: 4, fontSize: 11, color: '#6b7280' }}>
                                            <div>Amount ($)</div><div>Every</div><div>Frequency</div><div>Mode</div>
                                          </div>
                                          <div style={{ display: 'grid', gridTemplateColumns: '0.7fr 0.5fr 0.7fr 70px', gap: 8, alignItems: 'center', marginBottom: isFixed ? 4 : 8 }}>
                                            <div>
                                              {!isFixed ? (
                                                <input type="number" step="0.01" className="control-select" style={{ width: '100%' }} placeholder="0.00"
                                                  value={draftAssetAmounts['__self__'] ?? ''}
                                                  onChange={e => setDraftAssetAmounts(prev => ({ ...prev, '__self__': e.target.value }))} />
                                              ) : <div style={{ fontSize: 11, color: '#9ca3af' }}>Monthly values below</div>}
                                            </div>
                                            <div>
                                              {!isFixed ? (
                                                <input type="number" min="1" step="1" className="control-select" style={{ width: '100%' }} placeholder="1"
                                                  value={frequencies[key]?.count ?? '1'}
                                                  onChange={e => { const nv = e.target.value; setFrequencies(prev => ({ ...prev, [key]: { ...(prev[key] || {}), count: nv } })); }} />
                                              ) : <div style={{ textAlign: 'center', color: '#6b7280' }}>—</div>}
                                            </div>
                                            <div>
                                              {!isFixed ? (
                                                <select className="control-select" style={{ width: '100%' }}
                                                  value={frequencies[key]?.unit ?? 'months'}
                                                  onChange={e => { const nu = e.target.value; setFrequencies(prev => ({ ...prev, [key]: { ...(prev[key] || {}), unit: nu } })); }}>
                                                  <option value="days">days</option>
                                                  <option value="weeks">weeks</option>
                                                  <option value="months">months</option>
                                                  <option value="years">years</option>
                                                </select>
                                              ) : <div style={{ textAlign: 'center', color: '#6b7280' }}>—</div>}
                                            </div>
                                            <div>
                                              <select className="control-select" style={{ width: '100%', fontSize: 12 }}
                                                value={isFixed ? 'fixed' : 'period'}
                                                onChange={e => {
                                                  const v = e.target.value;
                                                  setDraftAssetChildMode(prev => ({ ...prev, '__self__': v }));
                                                  if (v !== 'fixed') {
                                                    const overrides = draftAssetMonthlyOverrides['__self__'] || {};
                                                    const months = getBudgetMonths();
                                                    if (months.length) {
                                                      const avg = months.reduce((s, m) => s + parseFloat(overrides[m] || 0), 0) / months.length;
                                                      setDraftAssetAmounts(prev => ({ ...prev, '__self__': avg ? String(Number(avg.toFixed(2))) : '' }));
                                                    }
                                                  } else {
                                                    setDraftAssetAmounts(prev => ({ ...prev, '__self__': '' }));
                                                  }
                                                }}>
                                                <option value="period">Period</option>
                                                <option value="fixed">Fixed</option>
                                              </select>
                                            </div>
                                          </div>
                                          {isFixed && (
                                            <div style={{ padding: '4px 0 10px 0px', background: '#060d17', marginBottom: 6, borderRadius: 4, padding: '8px' }}>
                                              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6 }}>Monthly values</div>
                                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 10 }}>
                                                {getBudgetMonths().map(month => (
                                                  <div key={month}>
                                                    <label style={{ fontSize: 10, display: 'block', marginBottom: 2, color: '#6b7280' }}>{month}</label>
                                                    <input type="number" step="0.01" className="control-select" style={{ width: '100%', height: 28, fontSize: 12 }}
                                                      value={draftAssetMonthlyOverrides['__self__']?.[month] ?? ''}
                                                      onChange={e => {
                                                        const v = e.target.value;
                                                        setDraftAssetMonthlyOverrides(prev => ({ ...prev, '__self__': { ...(prev['__self__'] || {}), [month]: v } }));
                                                        const months2 = getBudgetMonths();
                                                        if (months2.length) {
                                                          const updatedOvr = { ...(draftAssetMonthlyOverrides['__self__'] || {}), [month]: v };
                                                          const avg = months2.reduce((s, m) => s + parseFloat(updatedOvr[m] || 0), 0) / months2.length;
                                                          setDraftAssetAmounts(prev => ({ ...prev, '__self__': avg ? String(Number(avg.toFixed(2))) : '' }));
                                                        }
                                                      }}
                                                      placeholder="0.00" />
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </React.Fragment>
                                      );
                                    })()
                                  ) : (
                                    // ── Multi-child form (automotive / home) ──
                                    <>
                                      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.7fr 0.5fr 0.7fr 70px', gap: 8, marginBottom: 4, fontSize: 11, color: '#6b7280' }}>
                                        <div>Category</div><div>Amount ($)</div><div>Every</div><div>Frequency</div><div>Mode</div>
                                      </div>
                                      {children.map(ch => {
                                        const isFixed = draftAssetChildMode[ch.value] === 'fixed';
                                        return (
                                          <React.Fragment key={ch.value}>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.7fr 0.5fr 0.7fr 70px', gap: 8, alignItems: 'center', marginBottom: isFixed ? 4 : 6 }}>
                                              <div style={{ color: '#d1d5db', fontSize: 13 }}>↳ {ch.label}</div>
                                              <div>
                                                {!isFixed ? (
                                                  <input type="number" step="0.01" className="control-select" style={{ width: '100%' }} placeholder="0.00"
                                                    value={draftAssetAmounts[ch.value] ?? ''}
                                                    onChange={e => setDraftAssetAmounts(prev => ({ ...prev, [ch.value]: e.target.value }))} />
                                                ) : <div style={{ fontSize: 11, color: '#9ca3af' }}>Monthly values below</div>}
                                              </div>
                                              <div>
                                                {!isFixed ? (
                                                  <input type="number" min="1" step="1" className="control-select" style={{ width: '100%' }} placeholder="1"
                                                    value={frequencies[ch.value]?.count ?? '1'}
                                                    onChange={e => { const nv = e.target.value; setFrequencies(prev => ({ ...prev, [ch.value]: { ...(prev[ch.value] || {}), count: nv } })); }} />
                                                ) : <div style={{ textAlign: 'center', color: '#6b7280' }}>—</div>}
                                              </div>
                                              <div>
                                                {!isFixed ? (
                                                  <select className="control-select" style={{ width: '100%' }}
                                                    value={frequencies[ch.value]?.unit ?? 'months'}
                                                    onChange={e => { const nu = e.target.value; setFrequencies(prev => ({ ...prev, [ch.value]: { ...(prev[ch.value] || {}), unit: nu } })); }}>
                                                    <option value="days">days</option>
                                                    <option value="weeks">weeks</option>
                                                    <option value="months">months</option>
                                                    <option value="years">years</option>
                                                  </select>
                                                ) : <div style={{ textAlign: 'center', color: '#6b7280' }}>—</div>}
                                              </div>
                                              <div>
                                                <select className="control-select" style={{ width: '100%', fontSize: 12 }}
                                                  value={isFixed ? 'fixed' : 'period'}
                                                  onChange={e => {
                                                    const v = e.target.value;
                                                    setDraftAssetChildMode(prev => ({ ...prev, [ch.value]: v }));
                                                    if (v !== 'fixed') {
                                                      // switching back to period — clear overrides, restore avg as amount
                                                      const overrides = draftAssetMonthlyOverrides[ch.value] || {};
                                                      const months = getBudgetMonths();
                                                      if (months.length) {
                                                        const avg = months.reduce((s, m) => s + parseFloat(overrides[m] || 0), 0) / months.length;
                                                        setDraftAssetAmounts(prev => ({ ...prev, [ch.value]: avg ? String(Number(avg.toFixed(2))) : '' }));
                                                      }
                                                    } else {
                                                      setDraftAssetAmounts(prev => ({ ...prev, [ch.value]: '' }));
                                                    }
                                                  }}>
                                                  <option value="period">Period</option>
                                                  <option value="fixed">Fixed</option>
                                                </select>
                                              </div>
                                            </div>
                                            {isFixed && (
                                              <div style={{ padding: '4px 0 10px 20px', background: '#060d17', marginBottom: 6 }}>
                                                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6 }}>Monthly values for {ch.label}</div>
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 10 }}>
                                                  {getBudgetMonths().map(month => (
                                                    <div key={month}>
                                                      <label style={{ fontSize: 10, display: 'block', marginBottom: 2, color: '#6b7280' }}>{month}</label>
                                                      <input type="number" step="0.01" className="control-select" style={{ width: '100%', height: 28, fontSize: 12 }}
                                                        value={draftAssetMonthlyOverrides[ch.value]?.[month] ?? ''}
                                                        onChange={e => {
                                                          const v = e.target.value;
                                                          setDraftAssetMonthlyOverrides(prev => ({ ...prev, [ch.value]: { ...(prev[ch.value] || {}), [month]: v } }));
                                                          // recompute avg → draftAssetAmounts for this child
                                                          const months = getBudgetMonths();
                                                          if (months.length) {
                                                            const updatedOverrides = { ...(draftAssetMonthlyOverrides[ch.value] || {}), [month]: v };
                                                            const avg = months.reduce((s, m) => s + parseFloat(updatedOverrides[m] || 0), 0) / months.length;
                                                            setDraftAssetAmounts(prev => ({ ...prev, [ch.value]: avg ? String(Number(avg.toFixed(2))) : '' }));
                                                          }
                                                        }}
                                                        placeholder="0.00" />
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                          </React.Fragment>
                                        );
                                      })}
                                    </>
                                  )}
                                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                    <button className="btn-primary" style={{ padding: '4px 16px', fontSize: 13 }}
                                      onClick={async () => {
                                        const aid = String(selectedNewAssetId);
                                        // Build the payload for this asset — include child modes and monthly overrides
                                        const assetPayload = { ...draftAssetAmounts };
                                        // Attach child mode, overrides, and frequency as metadata keys so they round-trip through JSON
                                        const childModeSnapshot = { ...draftAssetChildMode };
                                        const childOverridesSnapshot = { ...draftAssetMonthlyOverrides };
                                        Object.entries(childModeSnapshot).forEach(([slug, mode]) => {
                                          assetPayload[`__mode__${slug}`] = mode;
                                        });
                                        Object.entries(childOverridesSnapshot).forEach(([slug, overrides]) => {
                                          assetPayload[`__overrides__${slug}`] = overrides;
                                        });
                                        // Save per-child frequency so the aggregate can normalize correctly
                                        if (isLeafAsset) {
                                          const freq = frequencies[key] || { count: '1', unit: 'months' };
                                          assetPayload['__mode____self__'] = draftAssetChildMode['__self__'] || 'period';
                                          assetPayload['__overrides____self__'] = draftAssetMonthlyOverrides['__self__'] || {};
                                          assetPayload['__freq____self__'] = { count: freq.count, unit: freq.unit };
                                        } else {
                                          children.forEach(ch => {
                                            const freq = frequencies[ch.value] || { count: '1', unit: 'months' };
                                            assetPayload[`__freq__${ch.value}`] = { count: freq.count, unit: freq.unit };
                                          });
                                        }
                                        // Save draft into assetAmounts (handles both new and edit-in-place)
                                        const nextAsset = {
                                          ...assetAmounts,
                                          [key]: { ...(assetAmounts[key] || {}), [aid]: assetPayload }
                                        };
                                        setAssetAmounts(nextAsset);
                                        setSavedAssetIds(prev => ({ ...prev, [key]: [...new Set([...(prev[key] || []), aid])] }));
                                        setAddingAssetFor(null);
                                        setSelectedNewAssetId('');
                                        setDraftAssetAmounts({});
                                        setDraftAssetChildMode({});
                                        setDraftAssetMonthlyOverrides({});
                                        recomputeParentFromAssets(key, children, nextAsset, frequencies);
                                        // Auto-save to backend so data persists without requiring "Save Changes"
                                        setSaving(true); setSaveError('');
                                        try { await buildAndPatchBudget(nextAsset); }
                                        catch (e) { const msg = e.response?.data || e.message; setSaveError(typeof msg === 'string' ? msg : JSON.stringify(msg)); }
                                        finally { setSaving(false); }
                                      }}>
                                      Save {assetLabelCap}
                                    </button>
                                    <button className="btn btn-secondary" style={{ padding: '4px 12px', fontSize: 13 }}
                                      onClick={() => {
                                        // Cancel: just close the draft form — savedAssetIds is untouched so the card re-appears
                                        setAddingAssetFor(null);
                                        setSelectedNewAssetId('');
                                        setDraftAssetAmounts({});
                                        setDraftAssetChildMode({});
                                        setDraftAssetMonthlyOverrides({});
                                      }}>
                                      Cancel
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          ) : availableAssets.length > 0 ? (
                            <button className="btn btn-secondary" style={{ fontSize: 13, padding: '5px 14px', marginTop: savedIds.length > 0 ? 6 : 0 }}
                              onClick={() => {
                                setAddingAssetFor(key);
                                setSelectedNewAssetId('');
                                setDraftAssetAmounts(isLeafAsset ? { '__self__': '' } : Object.fromEntries(children.map(ch => [ch.value, ''])));
                              }}>
                              + Add {assetLabelCap}
                            </button>
                          ) : savedIds.length > 0 ? (
                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>All {assetLabel}s have been budgeted.</div>
                          ) : (
                            <div style={{ fontSize: 12, color: '#6b7280' }}>No {assetLabel}s found. Add them in Balance Sheet first.</div>
                          )}
                        </div>
                      );
                    }

                    // ── Standard (total) children rows ──
                    if (!expanded) return null;
                    return (
                      <div style={{ padding: '0 12px 8px 12px', background: '#0b1220', borderBottom: '1px solid #111827' }}>
                        {children.map(ch => {
                          const chSlug = ch.value;
                          return (
                            <React.Fragment key={chSlug}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.8fr 0.6fr 0.8fr 80px', gap: 8, alignItems: 'center', padding: '6px 0' }}>
                                <div style={{ color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 6 }}>↳ {ch.label}</div>
                                <div>
                                  {!useMonthlyOverrides[chSlug] ? (
                                    <input type="number" step="0.01" value={subAmounts[key]?.[chSlug] ?? ''} onChange={e => onChangeChildAmount(key, chSlug, e.target.value)} className="control-select" style={{ width: '100%' }} placeholder="0.00" />
                                  ) : <div style={{ fontSize: 11, color: '#9ca3af' }}>Monthly values below</div>}
                                </div>
                                <div>
                                  {!useMonthlyOverrides[chSlug] ? (
                                    <input type="number" min="1" step="1" value={(frequencies[chSlug]?.count) ?? '1'} onChange={e => { const nv = e.target.value; let nf = null; setFrequencies(prev => { nf = { ...prev, [chSlug]: { ...(prev[chSlug] || {}), count: nv } }; return nf; }); Promise.resolve().then(() => recomputeParentFromChildren(key, subAmounts, nf)); }} className="control-select" style={{ width: '100%' }} placeholder="1" />
                                  ) : <div style={{ textAlign: 'center' }}>—</div>}
                                </div>
                                <div>
                                  {!useMonthlyOverrides[chSlug] ? (
                                    <select className="control-select" value={(frequencies[chSlug]?.unit) ?? 'months'} onChange={e => { const nu = e.target.value; let nf = null; setFrequencies(prev => { nf = { ...prev, [chSlug]: { ...(prev[chSlug] || {}), unit: nu } }; return nf; }); Promise.resolve().then(() => recomputeParentFromChildren(key, subAmounts, nf)); }} style={{ width: '100%' }}>
                                      <option value="days">days</option><option value="weeks">weeks</option><option value="months">months</option><option value="years">years</option>
                                    </select>
                                  ) : <div style={{ textAlign: 'center' }}>—</div>}
                                </div>
                                <div>
                                  <select className="control-select" style={{ width: '100%', fontSize: 12 }} value={useMonthlyOverrides[chSlug] ? 'monthly' : 'fixed'} onChange={e => { const v = e.target.value; setUseMonthlyOverrides(prev => ({ ...prev, [chSlug]: v === 'monthly' })); }}>
                                    <option value="fixed">Fixed</option>
                                    <option value="monthly">Monthly</option>
                                  </select>
                                </div>
                              </div>
                              {useMonthlyOverrides[chSlug] && (
                                <div style={{ padding: '4px 0 10px 20px', background: '#060d17' }}>
                                  <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6 }}>Monthly values for {ch.label}</div>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 10 }}>
                                    {getBudgetMonths().map(month => (
                                      <div key={month}>
                                        <label style={{ fontSize: 10, display: 'block', marginBottom: 2, color: '#6b7280' }}>{month}</label>
                                        <input type="number" step="0.01" className="control-select" style={{ width: '100%', height: 28, fontSize: 12 }}
                                          value={monthlyOverrides[chSlug]?.[month] ?? ''}
                                          onChange={e => {
                                            const v = e.target.value;
                                            setMonthlyOverrides(prev => ({ ...prev, [chSlug]: { ...(prev[chSlug] || {}), [month]: v } }));
                                            Promise.resolve().then(() => {
                                              const months = getBudgetMonths();
                                              if (!months.length) return;
                                              const updatedOverrides = { ...(monthlyOverrides[chSlug] || {}), [month]: v };
                                              const childMonthlyAvg = months.reduce((s, m) => s + parseFloat(updatedOverrides[m] || 0), 0) / months.length;
                                              setSubAmounts(prev => {
                                                const next = { ...prev, [key]: { ...(prev[key] || {}), [chSlug]: String(Number(childMonthlyAvg.toFixed(2))) } };
                                                recomputeParentFromChildren(key, next, frequencies);
                                                return next;
                                              });
                                            });
                                          }}
                                          placeholder="0.00" />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    );
                  })()}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // ── Main render ──

  if (loading) return <div className="content-container"><div style={{ color: '#9ca3af' }}>Loading budget…</div></div>;
  if (error) return <div className="content-container"><div style={{ color: '#fca5a5' }}>{error}</div></div>;
  if (!budget) return <div className="content-container"><div style={{ color: '#fca5a5' }}>Budget not found.</div></div>;

  const displayName = (budget.name && String(budget.name).trim().length) ? budget.name : `Budget ${budget.start_date}`;

  return (
    <div className="content-container">
      {/* Back link */}
      <div style={{ marginBottom: 12 }}>
        <button
          className="btn btn-secondary"
          style={{ fontSize: 13, padding: '4px 12px' }}
          onClick={() => navigate('/income_statement/budgets')}
        >
          ← Back to Budgets
        </button>
      </div>

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ margin: 0 }}>{displayName}</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-primary" style={{ padding: '6px 18px' }} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
          <button className="btn btn-secondary" style={{ padding: '6px 14px', color: '#f87171' }} onClick={handleDelete}>Delete</button>
        </div>
      </div>

      {saveError && <div style={{ color: '#fca5a5', marginBottom: 12 }}>{saveError}</div>}

      {/* Two-column layout: main content + notes panel */}
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
        {/* Main budget content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {renderEditForm()}
        </div>

        {/* Notes panel */}
        <NotesPanel budgetId={id} />
      </div>
    </div>
  );
};

export default BudgetDetailPage;
